/**
 * 年度交易狀況統計組件
 * 顯示年度交易數據分析，包括交易次數、買賣金額、損益統計等
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Assessment,
  PieChart,
  CalendarMonth,
  EmojiEvents,
  Warning,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  PieChart as RePieChart,
  Pie,
  Cell,
} from 'recharts';
import { useAppContext } from '../context/AppContext';
import PageHeader from './common/PageHeader';
import { AnnualStatsService, AnnualTransactionSummary } from '../services/annualStatsService';
import { DecimalHelper } from '../utils/decimalHelper';

// Tab 面板組件
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const AnnualTransactionStats: React.FC = () => {
  const { state } = useAppContext();
  const { transactions, currentAccountId } = state;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [selectedYear, setSelectedYear] = useState<number>(() => new Date().getFullYear());
  const [activeTab, setActiveTab] = useState(0);

  // 獲取可用年份列表
  const availableYears = useMemo(() => {
    return AnnualStatsService.getAvailableYears(transactions);
  }, [transactions]);

  // 計算年度統計數據
  const annualStats = useMemo<AnnualTransactionSummary | null>(() => {
    if (!currentAccountId || transactions.length === 0) {
      return null;
    }
    return AnnualStatsService.calculateAnnualStats(transactions, selectedYear);
  }, [transactions, selectedYear, currentAccountId]);

  // 格式化金額
  const formatCurrency = (amount: number) => {
    return `NT$ ${DecimalHelper.formatAmount(amount, 0)}`;
  };

  // 顏色配置
  const COLORS = ['#1565c0', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4', '#795548'];

  if (!currentAccountId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          請先選擇交易帳戶
        </Typography>
      </Box>
    );
  }

  if (transactions.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          尚無交易記錄
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="年度交易統計"
        icon={Assessment}
        subtitle={`查看 ${selectedYear} 年度的交易狀況分析`}
        actions={
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel id="year-select-label">選擇年份</InputLabel>
            <Select
              labelId="year-select-label"
              value={selectedYear}
              label="選擇年份"
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year} 年
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      />

      {annualStats && (
        <>
          {/* 總覽卡片 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    總交易筆數
                  </Typography>
                  <Typography variant="h4" component="div">
                    {annualStats.totalTransactions} 筆
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      size="small" 
                      label={`買: ${annualStats.buyCount} / 賣: ${annualStats.sellCount}`}
                      color="primary"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    總買入金額
                  </Typography>
                  <Typography variant="h4" component="div" color="primary">
                    {formatCurrency(annualStats.totalBuyAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    總賣出金額
                  </Typography>
                  <Typography variant="h4" component="div" color="secondary">
                    {formatCurrency(annualStats.totalSellAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    淨利潤
                  </Typography>
                  <Typography 
                    variant="h4" 
                    component="div" 
                    color={annualStats.netProfit >= 0 ? 'success' : 'error'}
                  >
                    {formatCurrency(annualStats.netProfit)}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      size="small"
                      icon={annualStats.netProfit >= 0 ? <TrendingUp /> : <TrendingDown />}
                      label={annualStats.netProfit >= 0 ? '獲利' : '虧損'}
                      color={annualStats.netProfit >= 0 ? 'success' : 'error'}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* 標籤頁 */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons={isMobile ? 'auto' : false}
            >
              <Tab icon={<CalendarMonth />} label="月度趨勢" />
              <Tab icon={<PieChart />} label="類型分布" />
              <Tab icon={<EmojiEvents />} label="績效排行" />
              <Tab icon={<Assessment />} label="詳細數據" />
            </Tabs>

            {/* 月度趨勢 */}
            <TabPanel value={activeTab} index={0}>
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={annualStats.monthlyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthName" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip 
                      formatter={(value: number | string | Array<number | string> | undefined) =>
                        value !== undefined ? formatCurrency(Number(value)) : ''
                      }
                    />
                    <Legend />
                    <Bar 
                      yAxisId="left" 
                      dataKey="buyAmount" 
                      name="買入金額" 
                      fill="#1565c0" 
                    />
                    <Bar 
                      yAxisId="left" 
                      dataKey="sellAmount" 
                      name="賣出金額" 
                      fill="#4caf50" 
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="realizedGain"
                      name="已實現損益"
                      stroke="#ff9800"
                      strokeWidth={2}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </TabPanel>

            {/* 類型分布 */}
            <TabPanel value={activeTab} index={1}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" gutterBottom align="center">
                    交易類型分布
                  </Typography>
                  <Box sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie
                          data={annualStats.transactionTypeDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(props) => {
                            const { name, percent } = props;
                            return `${name} ${(percent ? (percent * 100).toFixed(1) : '0')}%`;
                          }}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="count"
                          nameKey="type"
                        >
                          {annualStats.transactionTypeDistribution.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RePieChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>交易類型</TableCell>
                          <TableCell align="right">筆數</TableCell>
                          <TableCell align="right">金額</TableCell>
                          <TableCell align="right">占比</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {annualStats.transactionTypeDistribution.map((item) => (
                          <TableRow key={item.type}>
                            <TableCell>{item.type}</TableCell>
                            <TableCell align="right">{item.count}</TableCell>
                            <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                            <TableCell align="right">{item.percentage.toFixed(1)}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </TabPanel>

            {/* 績效排行 */}
            <TabPanel value={activeTab} index={2}>
              <Grid container spacing={3}>
                {/* 最佳交易 */}
                {annualStats.bestTrade && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <EmojiEvents sx={{ mr: 1 }} />
                          <Typography variant="h6">最佳交易</Typography>
                        </Box>
                        <Typography variant="h5" gutterBottom>
                          {annualStats.bestTrade.symbol} {annualStats.bestTrade.name}
                        </Typography>
                        <Typography variant="body1">
                          獲利: {formatCurrency(annualStats.bestTrade.realizedGain)}
                        </Typography>
                        <Typography variant="body2">
                          買入價: {annualStats.bestTrade.buyPrice} / 賣出價: {annualStats.bestTrade.sellPrice}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* 最差交易 */}
                {annualStats.worstTrade && annualStats.worstTrade.realizedGain < 0 && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Warning sx={{ mr: 1 }} />
                          <Typography variant="h6">最差交易</Typography>
                        </Box>
                        <Typography variant="h5" gutterBottom>
                          {annualStats.worstTrade.symbol} {annualStats.worstTrade.name}
                        </Typography>
                        <Typography variant="body1">
                          虧損: {formatCurrency(Math.abs(annualStats.worstTrade.realizedGain))}
                        </Typography>
                        <Typography variant="body2">
                          買入價: {annualStats.worstTrade.buyPrice} / 賣出價: {annualStats.worstTrade.sellPrice}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* 獲利股票排行 */}
                {annualStats.topGainers.length > 0 && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="h6" gutterBottom color="success.main">
                      獲利股票 TOP 5
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>股票</TableCell>
                            <TableCell align="right">交易次數</TableCell>
                            <TableCell align="right">已實現損益</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {annualStats.topGainers.map((stock) => (
                            <TableRow key={stock.symbol}>
                              <TableCell>
                                <strong>{stock.symbol}</strong> {stock.name}
                              </TableCell>
                              <TableCell align="right">
                                {stock.buyCount + stock.sellCount}
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'success.main' }}>
                                +{formatCurrency(stock.realizedGain)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                {/* 虧損股票排行 */}
                {annualStats.topLosers.length > 0 && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="h6" gutterBottom color="error.main">
                      虧損股票 TOP 5
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>股票</TableCell>
                            <TableCell align="right">交易次數</TableCell>
                            <TableCell align="right">已實現損益</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {annualStats.topLosers.map((stock) => (
                            <TableRow key={stock.symbol}>
                              <TableCell>
                                <strong>{stock.symbol}</strong> {stock.name}
                              </TableCell>
                              <TableCell align="right">
                                {stock.buyCount + stock.sellCount}
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'error.main' }}>
                                {formatCurrency(stock.realizedGain)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            {/* 詳細數據 */}
            <TabPanel value={activeTab} index={3}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>月份</TableCell>
                      <TableCell align="right">買入筆數</TableCell>
                      <TableCell align="right">賣出筆數</TableCell>
                      <TableCell align="right">股利筆數</TableCell>
                      <TableCell align="right">買入金額</TableCell>
                      <TableCell align="right">賣出金額</TableCell>
                      <TableCell align="right">已實現損益</TableCell>
                      <TableCell align="right">手續費</TableCell>
                      <TableCell align="right">稅金</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {annualStats.monthlyStats.map((month) => (
                      <TableRow key={month.month}>
                        <TableCell>{month.monthName}</TableCell>
                        <TableCell align="right">{month.buyCount}</TableCell>
                        <TableCell align="right">{month.sellCount}</TableCell>
                        <TableCell align="right">{month.dividendCount}</TableCell>
                        <TableCell align="right">{formatCurrency(month.buyAmount)}</TableCell>
                        <TableCell align="right">{formatCurrency(month.sellAmount)}</TableCell>
                        <TableCell 
                          align="right"
                          sx={{ color: month.realizedGain >= 0 ? 'success.main' : 'error.main' }}
                        >
                          {formatCurrency(month.realizedGain)}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(month.fees)}</TableCell>
                        <TableCell align="right">{formatCurrency(month.taxes)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default AnnualTransactionStats;
